/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/** 与 abrain-api-gateway 路由 path_pattern `/api/gateway/kz/**` 对齐 */
export const AGENT_API_GATEWAY_PREFIX_DEFAULT = '/api/gateway/kz';

const ROOT_API_PREFIX_PATHS = /^\/(v1|v2|v3|open_api)\//;

/** 不经网关转发，直连 Coze 后端（网关会校验登录态，未登录无法调登录接口） */
const DIRECT_API_PATH_PREFIXES = ['/api/passport/web/uc/login'] as const;

function normalizePrefix(raw: string): string {
  return raw.trim().replace(/\/$/, '');
}

/**
 * 读取环境变量 VITE_AGENT_API_GATEWAY_PREFIX。
 * 生产环境在 frontend/apps/coze-studio/.env.production 配置；
 * 未配置或设为 false/0/空字符串时不加前缀。
 */
export function getAgentApiGatewayPrefix(): string {
  if (typeof import.meta === 'undefined') {
    return '';
  }
  const raw = import.meta.env.VITE_AGENT_API_GATEWAY_PREFIX;
  if (raw === 'false' || raw === '0' || raw === '' || raw === undefined) {
    return '';
  }
  if (typeof raw === 'string' && raw.trim()) {
    return normalizePrefix(raw);
  }
  return '';
}

/**
 * 将 Coze 相对 API 路径加上网关前缀，例如：
 * `/api/plugin_api/foo` → `/api/gateway/kz/plugin_api/foo`
 * `/v1/bots/foo`         → `/api/gateway/kz/v1/bots/foo`
 */
export function resolveAgentApiGatewayUrl(url: string): string {
  const prefix = getAgentApiGatewayPrefix();
  if (!prefix || !url.startsWith('/')) {
    return url;
  }
  if (url.startsWith(`${prefix}/`) || url === prefix) {
    return url;
  }
  if (url.startsWith('/uc-api')) {
    return url;
  }
  if (DIRECT_API_PATH_PREFIXES.some(pathPrefix => url.startsWith(pathPrefix))) {
    return url;
  }

  if (url.startsWith('/api/')) {
    return `${prefix}/${url.slice('/api/'.length)}`;
  }
  if (ROOT_API_PREFIX_PATHS.test(url)) {
    return `${prefix}${url}`;
  }
  return url;
}
